<template>
  <el-radio
    border
    v-show="showGooglePayBtn"
    label="googlePay"
    id="googlePayBtn"
    v-loading="loading"
    :disabled="this.amount_to_pay <= 0"
  >
    <div class="flex items-center">
      <img
        class="googleImage"
        src="@/assets/payment/google_pay.svg"
        alt="google_pay"
      />
    </div>
  </el-radio>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { usePaymentStore } from "@/stores/PaymentStore.ts";
import { getPublishableKey } from "@/utils/stripeKeySelector";
import { loadStripe } from "@stripe/stripe-js";
// import { getCountryCode } from "@/components/shippingAddress/shippingAddressData/shippingAddressInputsStructure.js";
export default {
  name: "GooglePayButton",
  data() {
    return {
      publishableKey: null,
      showGooglePayBtn: false,
      loading: false,
      stripe: null,
      paymentRequest: null,
    };
  },
  async mounted() {
    this.publishableKey = getPublishableKey(this.customer_id, this.provider_has_revenue);
    if (this.amount_to_pay) {
      // if the amount to pay is not 0, then load stripe
      try {
        this.stripe = await loadStripe(this.publishableKey);
        if (this.stripe) {
          this.stripeJS();
        } else {
          console.error("Failed to initialize Stripe.");
        }
      } catch (err) {
        console.error("Failed to load Stripe:", err);
      }
    }
  },
  computed: {
    ...mapState(()=>usePaymentStore(), [
      'customer_id',
      'provider_has_revenue',
      "billingInfo",
      "sample_id",
      "accession_id",
      "order_id",
      "is_international_payment",
      'is_family_order',
    ]),
    amount_to_pay(){
      // won't consider amount_to_pay, since GooglePay method doesn't support to pay with fixed amount.
      return this.is_family_order ? Number(this.billingInfo?.family_orders?.patient_total ?? 0) : Number(this.billingInfo?.patient_bill?.total ?? 0);
    },
    converted_amount_to_pay() {
      // some currencies don't have decimal places, so we need to convert the amount to pay to the correct format
      const zeroDecimalCurrencies = ["jpy", "bif", 'clp', 'gnf', 'kmf', 'krw', 'mga', 'pyg', 'rwf', 'ugx', 'vnd', 'vuv', 'xaf', 'xof', 'xpf'];
      let currency = this.billingInfo?.currency?.toLowerCase() || "usd";
      if (zeroDecimalCurrencies.includes(currency)) {
        return Math.floor(this.amount_to_pay); // round to the nearest integer for zero-decimal currencies
      } else {
        // otherwise, convert to cents
        return Math.floor(this.amount_to_pay * 100);
      }
    },
  },
  methods: {
    ...mapActions(()=>usePaymentStore(), [
      "generateStripeDigitalWalletPayIntent",
    ]),
    stripeJS() {
      this.paymentRequest = this.stripe.paymentRequest({
        country: "US", // use our stripe account country code
        currency: (this.billingInfo?.currency || "usd").toLowerCase(),
        total: {
          label:
            "SampleID: " +
            this.sample_id +
            ",AccessionID: " +
            this.accession_id +
            ",ChargeTypeID: " +
            this.order_id,
          amount: this.converted_amount_to_pay,
        },
        disableWallets: ["applePay", "browserCard", "link"],
      });
      this.paymentRequest.canMakePayment().then((result) => {
        if (result) {
          console.log(result, "result");
          if (result.googlePay) {
            this.showGooglePayBtn = true;
            return;
          }
        } else {
          console.log(result, "no result");
          console.log("error");
        }
      });
      document.getElementById("googlePayBtn")?.addEventListener("click", async() => {
          if (this.amount_to_pay > 0) {
            // if the amount to pay is larger than 0, then show the payment request
            this.paymentRequest.show();
          } else {
            return;
          }
        });
      this.paymentRequest.on("paymentmethod", async (e) => {
        try {
          let clientSecret = await this.generateStripeDigitalWalletPayIntent();
          const { paymentIntent, error } = await this.stripe.confirmCardPayment(
            clientSecret,
            {
              payment_method: e.paymentMethod.id,
            },
            {
              handleActions: false,
            }
          );
          if (error) {
            e.complete("fail");
            this.$router.push("/payment_failed");
          }
          if (paymentIntent) {
            e.complete("success");
            this.$router.push("/payment_succeed");
          }
        } catch (error) {
          console.log(error);
          this.$router.push("/payment_failed");
        }
      });
    },
  },
};
</script>

<style scoped>
.el-radio-group {
  display: flex;
  margin: 0px;
  padding: 0px;
  justify-content: space-between;
  flex-wrap: wrap;
}

:deep(.el-radio.is-bordered) {
  width: 27vw;
  /* min-width: 284px; */
  height: 80px;
  border-radius: 4px;
  border: 1px solid #bcccdc;
  margin-bottom: 15px;
  margin-right: 0px;
}

:deep(.el-radio__label) {
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0.46px;
  color: #000000;
}

:deep(.el-radio.is-bordered.is-checked) {
  border: 1px solid #20a8c3;
}

:deep(.el-radio__input.is-checked + .el-radio__label) {
  font-weight: 500;
  color: #20a8c3;
}

:deep(.el-radio__inner) {
  height: 20px !important;
  width: 20px !important;
  margin-left: 23px;
  margin-right: 23px;
  border: 2px #000000 solid !important;
}

:deep(.el-radio.is-disabled .el-radio__inner) {
  border: 2px #c4c4c4 solid !important;
}

:deep(.el-radio__input.is-checked .el-radio__inner) {
  border: 2px #20a8c3 solid !important;
  background: transparent !important;
}

:deep(.el-radio__input.is-checked .el-radio__inner::after) {
  content: "";
  display: block;
  width: 10px;
  height: 10px;
  background-color: #20a8c3;
  border-radius: 50%;
  box-sizing: border-box;
  border: 2px #20a8c3 solid !important;
}

.googleImage,
.appleImage,
.cardImage {
  margin-right: 8px;
}

.radioImage {
  margin-right: 16px;
}

@media only screen and (max-width: 1024px) {
  .el-radio-group {
    display: flex;
    flex-direction: column;
    margin: 0px;
    padding: 0px;
    flex-wrap: nowrap;
  }

  .el-radio {
    margin-right: 0px;
    width: 100% !important;
    margin-bottom: 10px;
  }
}

@media only screen and (max-width: 767px) {
  .cardImage {
    margin-right: 4px;
    height: 12px;
  }

  .googleImage,
  .appleImage {
    margin-right: 8px;
    height: 20px;
  }

  .radioImage {
    margin-right: 8px;
    height: 20px;
  }

  .el-radio {
    height: 42px !important;
  }

  .el-radio div {
    font-size: 12px !important;
    font-weight: 400 !important;
  }

  :deep(.el-radio__inner) {
    height: 16px !important;
    width: 16px !important;
    margin-left: 12px;
    margin-right: 12px;
    border: 1px #000000 solid !important;
  }

  :deep(.el-radio__input.is-checked .el-radio__inner::after) {
    width: 8px;
    height: 8px;
    border: 1px #20a8c3 solid !important;
  }
}
</style>
